import { useEffect, useMemo, useRef } from 'react'
import { useApplyType } from './queries'
import { currentType, suggestType } from './issueType'
import type { Issue } from './github'

const APPLY_DELAY_MS = 600

// Auto-aplica el label de tipo semántico (bug/feature/idea/chore/refactor/docs)
// a issues que NO tengan ya un label semántico. Procesa secuencial con throttle.
// Los labels viven en GitHub — gestionados como set canónico por repo, ver
// rules/07-wipxap-github-labels.md.
//
// Dependencia estable: string de issueNumbers sin label. Refetches que no
// cambian la lista NO re-disparan el effect. Set `attempted` evita bucles.
export function useAutoLabel(repo: string, issues: Issue[] | undefined) {
  const apply = useApplyType(repo)
  const attempted = useRef<Set<number>>(new Set())
  const running = useRef(false)

  const pendingKey = useMemo(() => {
    if (!issues) return ''
    return issues
      .filter((i) => !currentType(i))
      .map((i) => i.number)
      .sort((a, b) => a - b)
      .join(',')
  }, [issues])

  useEffect(() => {
    if (!issues || !pendingKey) return
    if (running.current) return

    const pending = issues.filter(
      (i) => !currentType(i) && !attempted.current.has(i.number),
    )
    if (pending.length === 0) return

    let cancelled = false
    running.current = true

    void (async () => {
      for (const issue of pending) {
        if (cancelled) break
        attempted.current.add(issue.number)
        const type = suggestType(issue)
        try {
          await apply.mutateAsync({ issueNumber: issue.number, type })
        } catch {
          // Silencioso — no bloquear la cola si un issue falla (ej. permisos).
        }
        await sleep(APPLY_DELAY_MS)
      }
      running.current = false
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, pendingKey])
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}
